import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Trophy, ImageOff, RotateCw } from "lucide-react";
import { api, speak } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

function buildRounds(words) {
  return shuffle(words).slice(0, 6).map((w) => ({
    answer: w,
    options: shuffle([w, ...shuffle(words.filter((o) => o.word !== w.word)).slice(0, 2)]),
  }));
}

export default function ImageQuizPage() {
  const [words, setWords] = useState(null);
  const [seed, setSeed] = useState(0);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  useEffect(() => { api.get("/vocabulary").then((r) => setWords(r.data.filter((w) => w.image_url))); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rounds = useMemo(() => (words && words.length >= 3 ? buildRounds(words) : []), [words, seed]);
  const finished = rounds.length > 0 && idx >= rounds.length;

  useEffect(() => { if (finished) api.post("/vocabulary/session-complete").catch(() => {}); }, [finished]);

  const pick = (w) => {
    if (picked) return;
    setPicked(w);
    speak(rounds[idx].answer.word, 0.9);
    if (w.word === rounds[idx].answer.word) setScore((s) => s + 1);
  };
  const next = () => { setPicked(null); setIdx(idx + 1); };
  const restart = () => { setSeed(seed + 1); setIdx(0); setScore(0); setPicked(null); };

  const back = (
    <Link to="/vocabulaire" data-testid="image-quiz-back-btn" className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
      <ArrowLeft className="w-5 h-5" /> Mes mots
    </Link>
  );

  if (!words) return <Skeleton className="h-64 w-full" />;
  if (rounds.length === 0)
    return (
      <div className="space-y-5">
        {back}
        <div className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center text-slate-600" data-testid="image-quiz-empty">
          <ImageOff className="w-10 h-10 mx-auto mb-2 text-slate-400" />
          Il faut au moins 3 mots illustrés. Ouvrez « Mes mots » pour créer les images, puis revenez.
        </div>
      </div>
    );

  if (finished)
    return (
      <div className="space-y-6 text-center rise" data-testid="image-quiz-result">
        <div className="mx-auto w-24 h-24 rounded-full bg-amber-400 grid place-items-center shadow-lg">
          <Trophy className="w-12 h-12 text-slate-900" />
        </div>
        <p className="font-display text-7xl font-extrabold" data-testid="image-quiz-score">{score}/{rounds.length}</p>
        <p className="text-slate-600">Objectif fiches du jour validé.</p>
        <button onClick={restart} data-testid="image-quiz-restart-btn" className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display text-xl font-bold uppercase flex items-center justify-center gap-2 transition-colors">
          <RotateCw className="w-5 h-5" /> Rejouer
        </button>
        {back}
      </div>
    );

  const r = rounds[idx];
  const optCls = (o) => {
    if (!picked) return "bg-white border-slate-200 hover:border-orange-500";
    if (o.word === r.answer.word) return "bg-emerald-50 border-emerald-500";
    if (o.word === picked.word) return "bg-red-50 border-red-500";
    return "bg-white border-slate-200 opacity-50";
  };

  return (
    <div className="space-y-4" data-testid="image-quiz">
      <div className="flex items-center justify-between">
        {back}
        <span className="font-bold text-slate-600" data-testid="image-quiz-counter">{idx + 1} / {rounds.length}</span>
      </div>
      <h1 className="font-display text-3xl font-extrabold uppercase">Quel est ce mot ?</h1>
      <div className="mx-auto w-full max-w-xs aspect-square rounded-3xl overflow-hidden border-4 border-slate-900 bg-white shadow-lg">
        <img src={`${BACKEND}${r.answer.image_url}`} alt="Illustration à deviner" className="w-full h-full object-cover" data-testid="image-quiz-image" />
      </div>
      <div className="space-y-3">
        {r.options.map((o, i) => (
          <button
            key={o.word}
            onClick={() => pick(o)}
            disabled={!!picked}
            data-testid={`image-quiz-option-${i}`}
            className={`w-full min-h-14 rounded-xl border-2 px-4 py-3 text-left font-display text-2xl font-bold flex items-center justify-between transition-colors ${optCls(o)}`}
          >
            {o.word}
            {picked && o.word === r.answer.word && <CheckCircle2 className="w-7 h-7 text-emerald-600" />}
            {picked && o.word === picked.word && o.word !== r.answer.word && <XCircle className="w-7 h-7 text-red-600" />}
          </button>
        ))}
      </div>
      {picked && (
        <div className="space-y-3 rise">
          <p className="text-sm text-slate-600" data-testid="image-quiz-definition">{r.answer.definition}</p>
          <button onClick={next} data-testid="image-quiz-next-btn" className="w-full h-14 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-display text-xl font-bold uppercase transition-colors">
            {idx + 1 >= rounds.length ? "Voir mon score" : "Suivant"}
          </button>
        </div>
      )}
    </div>
  );
}
